/**
 * Streaming client for the authoring assistant.
 *
 * This deliberately bypasses the shared axios instance: axios buffers the whole response,
 * and `EventSource` cannot send an Authorization header (putting the JWT in the query
 * string would leak it into logs and history). `fetch` with a streamed body reader is the
 * only option that keeps the token in a header and still delivers tokens as they arrive.
 *
 * Conversation history lives in the agent session server-side, so turns are not re-sent.
 */

import type { ContentProposal } from '@shared/contentProposal'

import { API_BASE_URL, apiService } from './api'

export type AssistantEvent =
  | { type: 'status'; message: string }
  /** A tool the agent ran — shows which terms it actually grepped for. */
  | { type: 'tool'; name: string; detail?: string }
  /** What it returned, truncated. Display only — dropped before the transcript is saved. */
  | { type: 'toolResult'; text: string; isError?: boolean }
  | { type: 'proposal'; proposal: ContentProposal }
  | { type: 'delta'; text: string }
  /** Cumulative for the session, not the turn. `costMinorUnits` is minor units: 171 = $1.71. */
  | {
      type: 'usage'
      costMinorUnits?: number
      currency?: string
      inputTokens?: number
      outputTokens?: number
      cacheReadTokens?: number
      cacheCreationTokens?: number
    }
  | { type: 'done' }
  | { type: 'error'; message: string }

function authHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  }
}

/**
 * POST a request and yield the SSE events it streams back.
 *
 * Retries once through a token refresh — the axios interceptor handles that for every
 * other call, but this path is on its own.
 */
async function* streamSse<T>(path: string, body: unknown, signal: AbortSignal): AsyncGenerator<T> {
  const post = () =>
    fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      signal,
      credentials: 'include',
      headers: authHeaders(),
      body: JSON.stringify(body),
    })

  let response = await post()
  if (response.status === 401) {
    const refreshed = await apiService.refreshToken()
    if (refreshed.success) {
      localStorage.setItem('token', refreshed.data.token)
      response = await post()
    }
  }

  if (!response.ok || !response.body) {
    // Prefer the server's own message — it distinguishes "no API key" from "not provisioned".
    let message = `Assistant request failed (${response.status})`
    try {
      const parsed = await response.json()
      if (typeof parsed?.message === 'string' && parsed.message) {
        message = parsed.message
      }
    } catch {
      // Non-JSON error body — keep the status-code message.
    }
    yield { type: 'error', message } as T
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) {
      break
    }
    buffer += decoder.decode(value, { stream: true })

    // SSE frames are separated by a blank line; a frame may straddle two chunks.
    let boundary = buffer.indexOf('\n\n')
    while (boundary !== -1) {
      const frame = buffer.slice(0, boundary)
      buffer = buffer.slice(boundary + 2)
      if (frame.startsWith('data: ')) {
        try {
          yield JSON.parse(frame.slice(6)) as T
        } catch {
          // A malformed frame is not worth killing the stream over.
        }
      }
      boundary = buffer.indexOf('\n\n')
    }
  }
}

export interface AssistantTurnRequest {
  bookId: string
  /** Which conversation this belongs to — the agent session is warmed per conversation. */
  conversationId: string
  message: string
  /** The file the author is looking at, when there is one. */
  filename?: string
  /** A block inside what he marked — the server preloads the surrounding text from it. */
  startBlockId?: string
  /** The marked passage, sent live so unsaved edits are included. */
  markedText?: string
}

/** Send one message. Aborting closes the connection, which stops the agent turn. */
export function streamAssistantTurn(
  request: AssistantTurnRequest,
  signal: AbortSignal,
): AsyncGenerator<AssistantEvent> {
  const { bookId, ...body } = request
  return streamSse<AssistantEvent>(`/api/assistant/${bookId}/chat`, body, signal)
}

export interface ConversationSummary {
  conversationId: string
  title: string
  updatedAt: string
}

async function json<T>(path: string, init?: RequestInit): Promise<T | null> {
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      credentials: 'include',
      headers: authHeaders(),
      ...init,
    })
    if (!response.ok) {
      return null
    }
    const parsed = await response.json()
    return (parsed?.data ?? null) as T | null
  } catch {
    return null
  }
}

/** Recent conversations for this book, newest first. */
export async function listConversations(bookId: string): Promise<ConversationSummary[]> {
  return (await json<ConversationSummary[]>(`/api/assistant/${bookId}/conversations`)) ?? []
}

/** One conversation's stored transcript. */
export async function loadConversation(
  bookId: string,
  conversationId: string,
): Promise<{ timeline: unknown[]; title: string } | null> {
  return json(`/api/assistant/${bookId}/conversations/${encodeURIComponent(conversationId)}`)
}

/** Persist a conversation after a turn. Best-effort: a failed save must not break the chat. */
export async function saveConversation(
  bookId: string,
  conversationId: string,
  title: string,
  timeline: unknown[],
): Promise<void> {
  await json(`/api/assistant/${bookId}/conversations`, {
    method: 'PUT',
    body: JSON.stringify({ conversationId, title, timeline }),
  })
}

export async function deleteConversation(bookId: string, conversationId: string): Promise<void> {
  await json(`/api/assistant/${bookId}/conversations/${encodeURIComponent(conversationId)}`, {
    method: 'DELETE',
  })
}

/** Whether this deployment has the assistant configured. */
export async function fetchAssistantAvailability(bookId: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/assistant/${bookId}/availability`, {
      credentials: 'include',
      headers: authHeaders(),
    })
    if (!response.ok) {
      return false
    }
    const json = await response.json()
    return !!(json?.data?.available || json?.data?.proposals)
  } catch {
    return false
  }
}
